/**
 * This module provides centralized, type-safe access to all environment variables
 * used throughout the Axle partner portal application. It uses the env-var library
 * for runtime validation and type conversion of environment variables.
 */
import { AppEnvironment } from '@/constants/appEnvironment';
import envVar from 'env-var';

//////////////////////////////////////////////////////////////////////////////
// GUIDELINES FOR ENVIRONMENT VARIABLES
//////////////////////////////////////////////////////////////////////////////
//
// - DO NOT READ ENVIRONMENT VARIABLES ANYWHERE ELSE. DEFINE THEM HERE.
//
//   It is better to have a single, central, type-checked, and runtime-validated
//    place to see all the environment variables that the app uses.
//
//   In fact, eslint will mark your code as an error if you try to read from
//    process.env anywhere else.
//
// - USE THE CORRECT TYPE FOR THE ENVIRONMENT VARIABLE.
//
//   The 'env-var' package offers many different methods, like `.asString()`, `.asUrlString()`,
//    `.asPortNumber()`, `.asPositiveInt()`, etc. Use them correctly.
//
//   Documentation: https://github.com/evanshortiss/env-var/blob/HEAD/API.md
//
// - ONLY ADD SECRET VALUES TO THE .env FILE
//
//   The .env file is not committed to the repository because it contains secret values. To
//   mitigate having to update the .env for local development, the localDefaults object should
//   contain the default values (for local development) for any new env vars
//
// - AVOID SETTING DEFAULT VALUES.
//
//   When a default value is used in one of our non-local environments (e.g., production), it can
//   cause issues to covertly crop up when there's a typo or a missing env var. We want all values
//   to be required except where the default is unequivocally the correct option for non-local
//   environments, e.g., REACT_APP_FAKE_BEING_ONLINE = False
//
//   To set a default value for the local environment only, add it to the localDefaults object,
//   which will only be used in the local environment. NEVER ADD ANY SECRETS TO THIS FILE (in
//   localDefaults or otherwise).
//
// - PREFIX THE ENVIRONMENT VARIABLE WITH `REACT_APP_`.
//
//   Only vars with this prefix are exposed on import.meta.env — it is the
//    `envPrefix` configured in vite.config.ts.
//

// Vite exposes the app's env vars on import.meta.env. env-var expects a plain
// string dictionary, so read through this narrowed view.
const importMetaEnv = import.meta.env as unknown as Record<string, string | undefined>;

/**
 * The current Axle environment (e.g., 'local', 'development', 'staging', 'production').
 * This is used to determine environment-specific behavior throughout the application.
 *
 * @constant {string} axleEnvironment
 */
const axleEnvironment = envVar.from(importMetaEnv).get('REACT_APP_AXLE_ENV').required().asString();

/**
 * Boolean flag indicating whether the application is running in local development mode.
 * Used to enable/disable certain features and apply local-specific configurations.
 *
 * @constant {boolean} isLocal
 */
const isLocal = axleEnvironment === AppEnvironment.LOCAL;

/**
 * Type definition for local development default values.
 * These are non-secret environment variables that have safe defaults for local development.
 *
 * @interface LocalDefaults
 */
type LocalDefaults = {
    /** URL of the patient-facing application */
    REACT_APP_PATIENT_HOST: string;
    /** URL of the backend API server */
    REACT_APP_API_HOST: string;
    /** Whether to simulate being online when offline */
    REACT_APP_FAKE_BEING_ONLINE: string;
    /** Whether to enable React Query DevTools */
    REACT_APP_ENABLE_REACT_QUERY_DEVTOOLS: string;
};

/**
 * Default values for environment variables in local development.
 * These values are automatically applied when running in local environment.
 *
 * ⚠️ SECURITY WARNING: Never add secrets or sensitive data to this object!
 * Secrets should only be defined in the .env file.
 *
 * @constant {LocalDefaults} localDefaults
 */
const localDefaults: LocalDefaults = {
    // DO NOT ADD SECRETS!
    REACT_APP_PATIENT_HOST: 'http://localhost:3000',
    REACT_APP_API_HOST: 'http://localhost:8000',
    REACT_APP_FAKE_BEING_ONLINE: 'true',
    REACT_APP_ENABLE_REACT_QUERY_DEVTOOLS: 'true',
};

/**
 * The env accessor every value below reads from. In local development, the
 * localDefaults are layered underneath import.meta.env so missing dev vars fall
 * back to a safe value; in every other environment only the real env is used, so
 * a missing required var fails loudly instead of silently taking a default.
 *
 * DO NOT REMOVE THE isLocal CHECK — ONLY LOCAL SHOULD USE DEFAULTS!
 */
const env = envVar.from(isLocal ? { ...localDefaults, ...importMetaEnv } : importMetaEnv);

/**
 * Centralized environment variables configuration object.
 * All environment variables used throughout the application should be accessed through this object.
 *
 * The variables are organized into logical groups:
 * - Mandatory configuration: Required for basic app functionality
 * - Secrets: API keys and tokens (some optional for local development)
 * - Optional overrides: Feature flags and development tools
 *
 * @constant {Object} envVars
 */
export const envVars = {
    // ========================================================================
    // MANDATORY CONFIGURATION
    // ========================================================================

    /**
     * Current Axle environment (local, development, staging, production).
     * Used throughout the app to determine environment-specific behavior.
     */
    axleEnvironment: axleEnvironment,

    /**
     * URL of the patient-facing application.
     * Used for redirects and cross-app navigation.
     *
     * @example 'https://patient.axlehealth.com' or 'http://localhost:3000' (local)
     */
    patientHost: env.get('REACT_APP_PATIENT_HOST').required().asUrlString(),

    /**
     * URL of the backend API server.
     * All API requests are made to this host.
     *
     * @example 'https://api.axlehealth.com' or 'http://localhost:8000' (local)
     */
    backendHost: env.get('REACT_APP_API_HOST').required().asUrlString(),

    /**
     * Name of the CSRF cookie to read for CSRF token.
     * Defaults to 'csrftoken' (Django default) but can be customized per environment
     * (e.g., 'csrftoken_demo' in demo) to prevent cookie collisions between environments.
     *
     * @example 'csrftoken' (default) or 'csrftoken_demo' (demo environment)
     */
    csrfCookieName: env.get('REACT_APP_CSRF_COOKIE_NAME').default('csrftoken').asString(),

    // ========================================================================
    // SECRETS & API KEYS
    // ========================================================================

    /**
     * Google Maps API key for location services and map rendering.
     * Required for address autocomplete and map displays.
     *
     * @see https://developers.google.com/maps/documentation/javascript/get-api-key
     */
    googleMapsApiKey: env.get('REACT_APP_GOOGLE_MAPS_KEY').required().asString(),

    /**
     * MapBox access token for advanced mapping features.
     * Used as an alternative/supplement to Google Maps.
     *
     * @see https://docs.mapbox.com/help/getting-started/access-tokens/
     */
    mapBoxAccessKey: env.get('REACT_APP_MAP_BOX_KEY').required().asString(),

    /**
     * Datadog RUM (Real User Monitoring) application ID.
     * Optional - when empty, Datadog monitoring is disabled (useful for local development).
     *
     * @see https://docs.datadoghq.com/real_user_monitoring/browser/
     */
    datadogRumApplicationId: env.get('REACT_APP_DATADOG_RUM_APPLICATION_ID').default('').asString(),

    /**
     * Datadog RUM client token for browser monitoring.
     * Optional - when empty, Datadog monitoring is disabled (useful for local development).
     *
     * @see https://docs.datadoghq.com/real_user_monitoring/browser/
     */
    datadogRumClientToken: env.get('REACT_APP_DATADOG_RUM_CLIENT_TOKEN').default('').asString(),

    /**
     * Pylon chat widget application ID, from the widget's settings page.
     * Optional - when empty, the support widget never loads. Public: it identifies the
     * workspace to Pylon and is not a credential.
     *
     * @see https://docs.usepylon.com/pylon-docs/chat-widget/chat-setup
     */
    pylonAppId: env.get('REACT_APP_PYLON_APP_ID').default('').asString(),

    /**
     * Whether to load the Pylon support widget in local development.
     * By default it is disabled in local so test tickets stay out of the support queue.
     */
    pylonInLocal: env.get('REACT_APP_PYLON_IN_LOCAL').default('false').asBool(),

    /**
     * Release version (typically the short git SHA) injected at build time.
     * Passed to datadogRum.init as `version` so RUM error events can be
     * matched against the source maps uploaded by @datadog/vite-plugin.
     * Empty string when not built via CI.
     */
    releaseVersion: env.get('REACT_APP_RELEASE_VERSION').default('').asString(),

    // ========================================================================
    // OPTIONAL OVERRIDES & FEATURE FLAGS
    // ========================================================================

    /**
     * Whether to simulate being online when the browser is offline.
     * Useful for testing offline scenarios or working without internet.
     */
    fakeBeingOnline: env.get('REACT_APP_FAKE_BEING_ONLINE').default('false').asBool(),

    /**
     * Whether to enable Sentry error monitoring in local development.
     * By default, Sentry is disabled in local to prevent test data pollution.
     */
    sentryInLocal: env.get('REACT_APP_SENTRY_IN_LOCAL').default('false').asBool(),

    /**
     * Whether to enable Datadog monitoring in local development.
     * By default, Datadog is disabled in local to prevent test data pollution.
     */
    datadogInLocal: env.get('REACT_APP_DATADOG_IN_LOCAL').default('false').asBool(),

    /**
     * Whether to enable React Query DevTools for debugging API calls.
     * Provides a visual interface for inspecting queries, mutations, and cache.
     */
    enableReactQueryDevTools: env
        .get('REACT_APP_ENABLE_REACT_QUERY_DEVTOOLS')
        .default('false')
        .asBool(),

    /**
     * Passphrase that unlocks the feature flag override tools, which are unavailable in
     * production. Empty when an environment sets none, in which case the tools fall back to the
     * shared non-prod passphrase they ship with.
     */
    flagOverrideKey: env.get('REACT_APP_FLAG_OVERRIDE_KEY').default('').asString(),
};
